import {
  BasisConvention,
  CallableType,
  ClearingHouseType,
  CollateralIdentifierType,
  CollateralType,
  CollectionType,
  ColumnMappingString,
  CouponFrequency,
  Currency,
  DefaultHeading,
  Direction,
  EntityStatus,
  ExtendedTradingEntityType,
  HaircutType,
  IndexLag,
  MasterLegalAgreementType,
  MidTradeAction,
  MidTradeResponse,
  RateType,
  RelationshipAction,
  RepoType,
  SponsorType,
  SpreadsheetCsd,
  SpreadsheetMapping,
  SpreadsheetMappingType,
  SpreadsheetSettlementVenue,
  TradingEntityProductPermission,
  TradingType
} from 'api'
import {
  BasisConventionHelper,
  CallableTypeHelper,
  ClearingHouseTypeHelper,
  CollateralTypeHelper,
  CollectionTypeHelper,
  CouponFrequencyHelper,
  CurrencyHelper,
  DirectionHelper,
  EntityStatusHelper,
  HaircutTypeHelper,
  IndexLagHelper,
  MasterLegalAgreementTypeHelper,
  MidTradeActionHelper,
  MidTradeResponseHelper,
  RateTypeHelper,
  RelationshipActionHelper,
  RepoTypeHelper,
  SponsorTypeHelper,
  SpreadsheetCsdHelper,
  SpreadsheetSettlementVenueHelper,
  SYSTEM_USER_NAME,
  TradingEntityProductPermissionHelper,
  TradingEntityTypeHelper,
  TradingTypeHelper
} from 'model'
import { getEnumMappings } from './parseSpreadsheetUtils/utils'
import { NOTE_INDEXES } from '../../datagrid'

import { Locale, Translation } from 'locale'

export const getDefaultMapping = (type: SpreadsheetMappingType): SpreadsheetMapping => ({
  id: 'DEFAULT',
  name: 'Default Spreadsheet Mapping',
  userId: SYSTEM_USER_NAME,
  lastSetAsDefault: null,
  type,

  // Column mappings
  axe: { heading: DefaultHeading.Axe },
  bondPrice: { heading: DefaultHeading.BondPrice },
  book: { heading: DefaultHeading.Book },
  bundle: { heading: DefaultHeading.Bundle },
  callablePeriodDays: { heading: DefaultHeading.CallablePeriod },
  callableType: {
    heading: DefaultHeading.Callable,
    values: getEnumMappings(CallableType, CallableTypeHelper),
    defaultValue: null
  },
  counterpartyName: { heading: DefaultHeading.CounterpartyName },
  counterparty: { heading: DefaultHeading.Counterparty },
  collectionId: { heading: DefaultHeading.CollectionId },
  collectionName: { heading: DefaultHeading.CollectionName },
  collectionType: {
    heading: DefaultHeading.CollectionType,
    values: getEnumMappings(CollectionType, CollectionTypeHelper),
    defaultValue: null
  },
  csd: {
    defaultValue: null,
    heading: DefaultHeading.Csd,
    values: getEnumMappings(SpreadsheetCsd, SpreadsheetCsdHelper)
  },
  direction: {
    heading: DefaultHeading.Direction,
    values: getEnumMappings(Direction, DirectionHelper),
    defaultValue: null
  },
  endDate: { heading: DefaultHeading.EndDate, dateFormat: null },
  externalId: { heading: DefaultHeading.ExternalId },
  fund: { heading: DefaultHeading.Fund },
  isin: {
    heading: [SpreadsheetMappingType.COLLATERAL, SpreadsheetMappingType.COLLATERAL_BASKET_ALLOCATION].includes(type)
      ? DefaultHeading.Isin
      : DefaultHeading.IsinBui
  },
  secondaryIsin: { heading: DefaultHeading.SecondaryIsinBui },
  notes: NOTE_INDEXES.reduce<Record<string, ColumnMappingString>>((acc, index) => {
    acc[index] = { heading: `${DefaultHeading.Note} ${parseInt(index, 10) + 1}` }
    return acc
  }, {}),
  notional: { heading: DefaultHeading.Notional, decimalShift: null },
  preAgreed: { heading: DefaultHeading.PreAgreed },
  primeBroker: { heading: DefaultHeading.PrimeBroker },
  rate: { heading: DefaultHeading.Rate },
  fee: { heading: DefaultHeading.Fee },
  referenceRate: { heading: DefaultHeading.ReferenceRate },
  repoType: { heading: DefaultHeading.RepoType, values: getEnumMappings(RepoType, RepoTypeHelper), defaultValue: null },
  roll: { heading: DefaultHeading.Roll },
  sub: { heading: DefaultHeading.Sub },
  startConsideration: { heading: DefaultHeading.StartConsideration },
  startDate: { heading: DefaultHeading.StartDate, dateFormat: null },
  strategy: { heading: DefaultHeading.Strategy },
  term: { heading: DefaultHeading.Term },
  unwind: { heading: DefaultHeading.Unwind },
  settlementVenue: {
    heading: DefaultHeading.SettlementVenue,
    values: getEnumMappings(SpreadsheetSettlementVenue, SpreadsheetSettlementVenueHelper),
    defaultValue: null
  },
  clearingHouse: {
    heading: DefaultHeading.ClearingHouse,
    values: getEnumMappings(ClearingHouseType, ClearingHouseTypeHelper),
    defaultValue: null
  },
  sponsor: {
    heading: DefaultHeading.Sponsor,
    values: getEnumMappings(SponsorType, SponsorTypeHelper),
    defaultValue: null
  },

  // Incoming specific
  rfqId: { heading: DefaultHeading.RfqId },
  negotiationId: { heading: DefaultHeading.NegotiationId },
  haircutType: {
    heading: DefaultHeading.HaircutType,
    values: getEnumMappings(HaircutType, HaircutTypeHelper),
    defaultValue: null
  },

  // Mid Trade Event specific
  tradeId: { heading: DefaultHeading.TradeId },
  action: {
    heading: DefaultHeading.Action,
    values: getEnumMappings(MidTradeAction, MidTradeActionHelper),
    defaultValue: null
  },
  effectiveDate: { heading: DefaultHeading.EffectiveDate, dateFormat: null },
  haircut: { heading: DefaultHeading.Haircut },
  response: {
    heading: DefaultHeading.Response,
    values: getEnumMappings(MidTradeResponse, MidTradeResponseHelper),
    defaultValue: null
  },

  // Collateral specific
  tradingStatus: {
    heading: type === SpreadsheetMappingType.COLLATERAL ? DefaultHeading.TradingStatus : DefaultHeading.Status,
    values: getEnumMappings(EntityStatus, EntityStatusHelper),
    defaultValue: null
  },
  platform: {
    heading: DefaultHeading.Platform,
    values: {
      [CollateralIdentifierType.ISIN]: Locale.translate(Translation.TC_MARKET_EUROPE),
      [CollateralIdentifierType.CUSIP]: Locale.translate(Translation.TC_MARKET_US)
    },
    defaultValue: null
  },
  shortId: { heading: DefaultHeading.ShortId },
  fisn: { heading: DefaultHeading.Fisn },
  cfi: { heading: DefaultHeading.Cfi },
  cusip: { heading: DefaultHeading.Cusip },
  currency: { heading: DefaultHeading.Currency, values: getEnumMappings(Currency, CurrencyHelper), defaultValue: null },
  country: { heading: DefaultHeading.Country },
  sector: { heading: DefaultHeading.Sector },
  tradingType: {
    heading: DefaultHeading.TradingType,
    values: getEnumMappings(TradingType, TradingTypeHelper),
    defaultValue: null
  },
  cleanPrice: { heading: DefaultHeading.CleanPrice },
  ticketSize: { heading: DefaultHeading.TicketSize },
  issuer: { heading: DefaultHeading.Issuer },
  firstSettlementDate: { heading: DefaultHeading.FirstSettlementDate, dateFormat: null },
  accrualDate: { heading: DefaultHeading.AccrualDate, dateFormat: null },
  firstCouponDate: { heading: DefaultHeading.FirstCouponDate, dateFormat: null },
  maturityDate: { heading: DefaultHeading.MaturityDate, dateFormat: null },
  couponType: {
    heading: DefaultHeading.CouponType,
    values: getEnumMappings(RateType, RateTypeHelper),
    defaultValue: null
  },
  basisConvention: {
    heading: DefaultHeading.BasisConvention,
    values: getEnumMappings(BasisConvention, BasisConventionHelper),
    defaultValue: null
  },
  coupon: { heading: DefaultHeading.Coupon },
  frequency: {
    heading: DefaultHeading.Frequency,
    values: getEnumMappings(CouponFrequency, CouponFrequencyHelper),
    defaultValue: null
  },
  collateralType: {
    heading: DefaultHeading.CollateralType,
    values: getEnumMappings(CollateralType, CollateralTypeHelper),
    defaultValue: null
  },
  index: { heading: DefaultHeading.Index },
  lag: {
    heading: DefaultHeading.Lag,
    values: getEnumMappings(IndexLag, IndexLagHelper),
    defaultValue: null
  },
  baseIndexValue: { heading: DefaultHeading.BaseIndexValue },

  // Trading Entity specific
  organisation: {
    heading: type === SpreadsheetMappingType.TRADING_ENTITY_PRODUCT ? DefaultHeading.OrganisationShortCode : DefaultHeading.Organisation
  },
  publishedName: { heading: DefaultHeading.PublishedName },
  shortName: {
    heading: [SpreadsheetMappingType.TRADING_ENTITY_PRODUCT, SpreadsheetMappingType.FUND_ASSIGNMENT].includes(type)
      ? DefaultHeading.TradingEntityShortName
      : DefaultHeading.ShortName
  },
  ficcShortName: {
    heading: DefaultHeading.FiccShortName
  },
  rightOfExit: {
    heading: DefaultHeading.RightOfExit
  },
  rightOfSubstitution: {
    heading: DefaultHeading.RightOfSubstitution
  },
  lei: { heading: DefaultHeading.Lei },
  bic: { heading: DefaultHeading.Bic },
  tradingEntityType: {
    heading: DefaultHeading.TradingEntityType,
    values: getEnumMappings(ExtendedTradingEntityType, TradingEntityTypeHelper),
    defaultValue: null
  },

  // Trading Entity Product specific
  product: { heading: DefaultHeading.Product },
  permission: {
    heading: DefaultHeading.Permission,
    values: getEnumMappings(TradingEntityProductPermission, TradingEntityProductPermissionHelper),
    defaultValue: null
  },

  // Trading Relationship specific
  relationshipAction: {
    heading: DefaultHeading.RelationshipAction,
    values: getEnumMappings(RelationshipAction, RelationshipActionHelper),
    defaultValue: null
  },
  buyOrganisation: { heading: DefaultHeading.BuyOrganisation },
  buyTradingEntity: { heading: DefaultHeading.BuyTradingEntity },
  sellOrganisation: { heading: DefaultHeading.SellOrganisation },
  sellTradingEntity: { heading: DefaultHeading.SellTradingEntity },
  usSponsoredClearing: { heading: DefaultHeading.UsSponsoredClearing },
  usSponsoredClearingDefault: { heading: DefaultHeading.UsSponsoredClearingDefault },
  masterLegalAgreements: {
    heading: DefaultHeading.MasterLegalAgreements,
    values: getEnumMappings(MasterLegalAgreementType, MasterLegalAgreementTypeHelper),
    defaultValue: null
  },
  defaultAgreement: {
    heading: DefaultHeading.DefaultAgreement,
    values: getEnumMappings(MasterLegalAgreementType, MasterLegalAgreementTypeHelper),
    defaultValue: null
  },

  euGfidCode: { heading: DefaultHeading.EuGfidCode },
  usGfidCode: { heading: DefaultHeading.UsGfidCode },
  eligible15a6: { heading: DefaultHeading.Eligible15a6 },
  eligibleCombinedRecFile: { heading: DefaultHeading.EligibleCombinedRecFile },
  centralBank: { heading: DefaultHeading.CentralBank },
  negotiationHistoryFile: { heading: DefaultHeading.NegotiationHistoryFile },
  operatingCountry: { heading: DefaultHeading.OperatingCountry },

  // Collateral Basket Allocation
  allInPrice: { heading: DefaultHeading.AllInPrice, decimalShift: null },
  evergreenNoticePeriod: { heading: DefaultHeading.EvergreenNoticePeriod },
  version: 'default'
})
